import {BaseStore, globalStore, StoreObject} from "../../../stem-core/state/Store.js";
import {field} from "../../../stem-core/state/StoreField.js";
import {apiClient, type MerchantScopedRequest} from "../../connection/BlinkApiClient.js";
import {BaseEnum, makeEnum} from "../../../stem-core/state/BaseEnum.js";
import {formatPercentFromRatio} from "../../../stem-core/base/Formatting.js";
import {Merchant} from "../MerchantStore";
import {MerchantAudience} from "./MerchantAudienceStore";
import {StemDate} from "../../../stem-core/time/Date";
import type {StoreId} from "../../../stem-core/state/State";

@makeEnum
export class MerchantEmailCampaignStatus extends BaseEnum {
    static UNSCHEDULED;
    static VALIDATION_FAILED;
    static SCHEDULED;
    static STARTED;
    static STOPPED;
    static FINISHED;
}

export class EmailSendingStoreObject extends StoreObject {
    @field(Number) numSentEmails: number;
    @field(Number) numReadEmails: number;
    @field(Number) numBouncedEmails: number;
    @field(Number) numFailedEmails: number;
    @field(Number) totalNumReads: number;

    getAllFailed() {
        return this.numBouncedEmails + this.numFailedEmails;
    }

    formatOpenPercent() {
        return formatPercentFromRatio(this.numReadEmails, this.numSentEmails);
    }
}

@globalStore
export class MerchantEmailCampaign extends BaseStore("MerchantEmailCampaign", {}, EmailSendingStoreObject) {
    @field("Merchant") merchant: Merchant;
    @field("MerchantAudience") audience: MerchantAudience;
    @field(String) name: string;
    @field(Date) createdAt: StemDate;
    @field(Date) scheduledSendDate: StemDate;

    getAllEmailTemplates() {
        return this.getStore("EmailTemplate").filterBy({campaignId: this.id});
    }
}

// What MerchantCreateEmailCampaign validates (merchants/deserializers.py:322). Naming a base campaign
// clones its templates
export interface CreateEmailCampaignRequest extends MerchantScopedRequest {
    name: string;
    audienceId: StoreId;
    scheduledSendDate?: StemDate;
    fromEmailCampaignId?: StoreId;
}

export async function apiMerchantCreateEmailCampaign(request: CreateEmailCampaignRequest) {
    const response = await apiClient.post("/merchant/create_email_campaign/", request);
    return MerchantEmailCampaign.loadObject(response);
}

// What MerchantEditEmailCampaign validates (merchants/deserializers.py:329)
export interface EditEmailCampaignRequest extends MerchantScopedRequest {
    emailCampaignId: StoreId;
    name?: string;
    audienceId?: StoreId;
}

export async function apiMerchantEditEmailCampaign(request: EditEmailCampaignRequest) {
    return apiClient.post("/merchant/edit_email_campaign", request);
}

// What MerchantDeleteEmailCampaign validates (merchants/deserializers.py:335). No caller today
export interface DeleteEmailCampaignRequest extends MerchantScopedRequest {
    emailCampaignId: StoreId;
}

export async function apiMerchantDeleteEmailCampaign(request: DeleteEmailCampaignRequest) {
    return apiClient.post("/merchant/delete_email_campaign", request);
}
